import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { Debt } from "../lib/types";
import { DebtsList } from "../components/debts";
import {
  debtShareAPI,
  DebtShare,
  pageShareAPI,
  PageShareInfo,
  PageShareValidationResult,
} from "../services/api/api";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0f0f0f",
      paper: "#1a1a1a",
    },
  },
});

type DebtShareCardKey =
  | "balance"
  | "creditLimit"
  | "available"
  | "utilization"
  | "paydown"
  | "monthly"
  | "credits";

type DebtShareSettings = {
  hiddenDebtIds: string[];
  hiddenTypes: string[];
  totalExcludedTypes: string[];
  visibleCards: DebtShareCardKey[];
  hideLimits: boolean;
  hideDueDates: boolean;
  hideDateBorrowed: boolean;
  creditorNameMode: "full" | "partial" | "obscured";
  accountNameMode: "full" | "partial" | "obscured";
};

const defaultDebtSettings: DebtShareSettings = {
  hiddenDebtIds: [],
  hiddenTypes: [],
  totalExcludedTypes: ["Auto Loan", "Mortgage"],
  visibleCards: [
    "balance",
    "creditLimit",
    "available",
    "utilization",
    "paydown",
    "monthly",
    "credits",
  ],
  hideLimits: false,
  hideDueDates: false,
  hideDateBorrowed: false,
  creditorNameMode: "full",
  accountNameMode: "full",
};

const formatDateCell = (value?: Date | string | null): string => {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const formatPartialName = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length <= 3) return trimmed;
  return `${trimmed.slice(0, 3)}...`;
};

const buildObscuredMap = (
  values: string[],
  prefix: string
): Map<string, string> => {
  const unique = Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  return new Map(
    unique.map((value, index) => [value, `${prefix} ${index + 1}`])
  );
};

const getDisplayName = (
  value: string,
  mode: "full" | "partial" | "obscured",
  obscuredMap: Map<string, string>
) => {
  if (mode === "partial") return formatPartialName(value);
  if (mode === "obscured") return obscuredMap.get(value) || value;
  return value;
};

const SharedDebtsPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [shareInfo, setShareInfo] = useState<DebtShare | null>(null);
  const [pageShareInfo, setPageShareInfo] = useState<PageShareInfo | null>(
    null
  );
  const [password, setPassword] = useState("");
  const [debts, setDebts] = useState<Debt[]>([]);
  const [creditor, setCreditor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [debtShareSettings, setDebtShareSettings] =
    useState<DebtShareSettings>(defaultDebtSettings);

  const requiresPassword = Boolean(
    pageShareInfo?.requiresPassword || shareInfo?.requiresPassword
  );

  const loadShareInfo = useCallback(async () => {
    if (!shareId) {
      setError("Invalid share link");
      setLoading(false);
      return;
    }

    try {
      try {
        const info = await pageShareAPI.getInfo(shareId);
        if (info.pageType === "debts") {
          setPageShareInfo(info);
          return;
        }
        setError("Invalid share link");
      } catch (err) {
        const message = err instanceof Error ? err.message : "";
        if (message.includes("Share not found")) {
          const info = await debtShareAPI.getInfo(shareId);
          setShareInfo(info);
          return;
        }
        throw err;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load share");
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    void loadShareInfo();
  }, [loadShareInfo]);

  useEffect(() => {
    setDebts([]);
    setCreditor(null);
    setError(null);
    setIsAuthenticated(false);
    setDebtShareSettings(defaultDebtSettings);
    setLoading(true);
  }, [shareId]);

  useEffect(() => {
    if (pageShareInfo && !pageShareInfo.requiresPassword) {
      void pageShareAPI
        .validate(shareId!)
        .then((result: PageShareValidationResult) => {
          setDebts(result.debts || []);
          setCreditor(null);
          setDebtShareSettings({
            ...defaultDebtSettings,
            ...(result.payload?.debtSettings || {}),
          });
          setIsAuthenticated(true);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : "Validation failed");
        });
    }

    if (shareInfo && !shareInfo.requiresPassword) {
      void debtShareAPI
        .validate(shareId!, "")
        .then((result) => {
          setDebts(result.debts);
          setCreditor(result.creditor || null);
          setDebtShareSettings(defaultDebtSettings);
          setIsAuthenticated(true);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : "Validation failed");
        });
    }
  }, [pageShareInfo, shareId, shareInfo]);

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareId || !password) return;

    setValidating(true);
    setError(null);

    try {
      if (pageShareInfo) {
        const result = (await pageShareAPI.validate(
          shareId,
          password
        )) as PageShareValidationResult;
        setDebts(result.debts || []);
        setCreditor(null);
        setDebtShareSettings({
          ...defaultDebtSettings,
          ...(result.payload?.debtSettings || {}),
        });
        setIsAuthenticated(true);
      } else {
        const result = await debtShareAPI.validate(shareId, password);
        setDebts(result.debts);
        setCreditor(result.creditor || null);
        setDebtShareSettings(defaultDebtSettings);
        setIsAuthenticated(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid password");
    } finally {
      setValidating(false);
    }
  };

  const hiddenDebtIds = useMemo(
    () => new Set(debtShareSettings.hiddenDebtIds || []),
    [debtShareSettings.hiddenDebtIds]
  );

  const hiddenTypes = useMemo(
    () =>
      new Set(
        (debtShareSettings.hiddenTypes || []).map((type) => type.toLowerCase())
      ),
    [debtShareSettings.hiddenTypes]
  );

  const visibleDebts = useMemo(
    () =>
      debts.filter(
        (debt) =>
          !hiddenDebtIds.has(debt.id) &&
          !hiddenTypes.has((debt.type || "Other").toLowerCase())
      ),
    [debts, hiddenDebtIds, hiddenTypes]
  );

  const creditorNameMap = useMemo(
    () =>
      buildObscuredMap(
        visibleDebts.map((debt) => debt.creditor || "Unknown Creditor"),
        "Debtor"
      ),
    [visibleDebts]
  );

  const accountNameMap = useMemo(
    () =>
      buildObscuredMap(
        visibleDebts.map((debt) => debt.account || "Account"),
        "Account"
      ),
    [visibleDebts]
  );

  const displayDebts = useMemo(() => {
    return visibleDebts.map((debt) => {
      const displayedCreditor = getDisplayName(
        debt.creditor || "Unknown Creditor",
        debtShareSettings.creditorNameMode,
        creditorNameMap
      );
      const displayedAccount = getDisplayName(
        debt.account || "Account",
        debtShareSettings.accountNameMode,
        accountNameMap
      );

      return {
        ...debt,
        creditor: displayedCreditor,
        account: displayedAccount,
        creditLimit: debtShareSettings.hideLimits ? 0 : debt.creditLimit,
        dueDate: debtShareSettings.hideDueDates ? "" : debt.dueDate,
        dateBorrowed: debtShareSettings.hideDateBorrowed
          ? ""
          : debt.dateBorrowed,
      } as Debt;
    });
  }, [
    accountNameMap,
    creditorNameMap,
    debtShareSettings.accountNameMode,
    debtShareSettings.creditorNameMode,
    debtShareSettings.hideDateBorrowed,
    debtShareSettings.hideDueDates,
    debtShareSettings.hideLimits,
    visibleDebts,
  ]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", backgroundColor: "#0f0f0f", p: 3 }}>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" sx={{ color: "white", mb: 1 }}>
              Shared Debt Information
            </Typography>
            {(pageShareInfo?.expiresAt || shareInfo?.expiresAt) && (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.6)" }}
              >
                This link expires on{" "}
                {formatDateCell(
                  pageShareInfo?.expiresAt || shareInfo?.expiresAt
                )}
              </Typography>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error && !isAuthenticated ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Alert severity="error" sx={{ maxWidth: 400 }}>
                {error}
              </Alert>
            </Box>
          ) : !isAuthenticated && !requiresPassword ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : requiresPassword && !isAuthenticated ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <Card sx={{ maxWidth: 400, width: "100%" }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <LockIcon
                      sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                      Password Protected
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mt: 1,
                      }}
                    >
                      Enter the password to view the shared debt information
                    </Typography>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmitPassword}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={{ mb: 2 }}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={validating || !password}
                    >
                      {validating ? (
                        <CircularProgress size={24} />
                      ) : (
                        "View Debts"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <>
              {creditor && (
                <Typography
                  variant="h5"
                  sx={{ color: "white", mb: 3, textAlign: "center" }}
                >
                  Debts from: {creditor}
                </Typography>
              )}

              <DebtsList
                debts={displayDebts}
                expenses={[]}
                plaidLoading={false}
                readOnly={true}
                onEditDebt={() => {}}
                onDeleteDebt={() => {}}
                onLinkAccount={() => {}}
              />
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SharedDebtsPage;
